import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowLeft,
  ArrowUpRight,
  ArrowDownRight,
  Zap,
  Search,
  LayoutDashboard,
  ChartNoAxesCombined,
  Link,
  Settings,
  History,
  Volume2,
  VolumeX,
  X,
  Pin,
  RotateCcw,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  LoaderCircle,
  Copy,
  ExternalLink,
  LogOut,
} from "lucide-react";
import { api, post } from "./api.js";
import Chart from "./Chart.jsx";
import DatePicker from "./DatePicker.jsx";
import InstallBanner from "./InstallBanner.jsx";
import LocalGenerator,{LocalBrokerPage} from './LocalGenerator.jsx';
import { aggregate, filterHistory, saoDay } from "./statistics.js";
import { auditOriginalCriteria } from "./originalCriteria.js";
const activeStates = ["connecting", "getting_actives", "analyzing"];
const friendly = (n) =>
  Number(n).toLocaleString("pt-BR", { maximumFractionDigits: 2 });
const money = (n) =>
  Number(n).toLocaleString("pt-BR", { style: "currency", currency: "USD" });
function useRoute() {
  const [route, setRoute] = useState(location.pathname);
  useEffect(() => {
    const pop = () => setRoute(location.pathname);
    addEventListener("popstate", pop);
    return () => removeEventListener("popstate", pop);
  }, []);
  return [
    route,
    (path) => {
      history.pushState({}, "", path);
      setRoute(path);
    },
  ];
}
function Modal({ title, children, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const old = document.activeElement;
    ref.current?.focus();
    const key = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab") {
        const nodes = ref.current?.querySelectorAll(
          "button,input,select,a[href]",
        );
        if (!nodes?.length) return;
        const first = nodes[0],
          last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("keydown", key);
      old?.focus();
    };
  }, [onClose]);
  return (
    <div
      className="overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={ref}
      >
        <button className="icon close" aria-label="Fechar" onClick={onClose}>
          <X size={20} />
        </button>
        <h2>{title}</h2>
        {children}
      </section>
    </div>
  );
}
export default function App({onLogout}) {
  const [route, navigate] = useRoute(),
    [user, setUser] = useState(null),
    [state, setState] = useState({ status: "idle" }),
    [integration, setIntegration] = useState(null),
    [independentStatus,setIndependentStatus]=useState(null),
    [score, setScore] = useState({ win: 0, loss: 0 }),
    [error, setError] = useState(""),
    [boot, setBoot] = useState(true),
    [connected, setConnected] = useState(false),
    [dialog, setDialog] = useState(null),
    [busy, setBusy] = useState(false),
    [tts, setTts] = useState(false),
    [pinned, setPinned] = useState(
      localStorage.getItem("score-pinned") === "true",
    );
  const requestId = useRef(0),
    disposed = useRef(false);
  const load = useCallback(async () => {
    const id = ++requestId.current;
    const [status, source] = await Promise.all([
      api("/generator/status"),
      api("/brokers/integration"),
    ]);
    if (id === requestId.current && !disposed.current) {
      setState(status);
      setIntegration(source);
    }
  }, []);
  const refreshScore = useCallback(
    async () => {const next=await api("/mascot/score");if(!disposed.current)setScore(next);},
    [],
  );
  useEffect(() => {
    disposed.current = false;
    let socket, timer, retry;
    const connect = () => {
      if (disposed.current) return;
      socket = new WebSocket(
        `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`,
      );
      socket.onopen = () => {
        setConnected(true);
        socket.send(JSON.stringify({ type: "enter_signal_screen" }));
      };
      socket.onmessage = () => {
        load().catch(() => {});
        refreshScore().catch(() => {});
      };
      socket.onclose = () => {
        setConnected(false);
        if (!disposed.current) retry = setTimeout(connect, 2000);
      };
      socket.onerror = () => socket.close();
    };
    (async () => {
      try {
        const auth = await api("/auth/me");
        if (disposed.current) return;
        setUser(auth.user);
        setIntegration(await api("/brokers/integration"));
        await load();
        await refreshScore();
        if (disposed.current) return;
        setBoot(false);
        connect();
        timer = setInterval(
          () => {load().catch((e) => setError(e.message));refreshScore().catch(()=>{});},
          2000,
        );
      } catch (e) {
        if (!disposed.current) {
          setError(e.message);
          setBoot(false);
        }
      }
    })();
    return () => {
      disposed.current = true;
      clearInterval(timer);
      clearTimeout(retry);
      socket?.close();
    };
  }, [load, refreshScore]);
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);
  const action = async (fn) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const cancel = async () => {
    await post("/generator/cancel");
    requestId.current++;
    setState({ status: "idle" });
    speechSynthesis?.cancel();
    setTts(false);
  };
  const go = (path) =>
    action(async () => {
      if (path === "/app" && state.status !== "idle") await cancel();
      navigate(path);
    });
  const speak = () => {
    if (tts) {
      speechSynthesis.cancel();
      setTts(false);
      return;
    }
    const signal = state.signal;
    if (!signal) return;
    const phrase = `${signal.data_source==='nextgreen'?'Sinal NEXTGREEN.':'Reprodução histórica.'} ${signal.ativo_display}, ${signal.direcao}, confirmação ${signal.confirmacao.toFixed(1)} por cento. RSI ${signal.indicadores.rsi}, ADX ${signal.indicadores.adx}.`;
    const utterance = new SpeechSynthesisUtterance(phrase);
    utterance.lang = "pt-BR";
    utterance.onend = () => setTts(false);
    utterance.onerror = () => setTts(false);
    speechSynthesis.speak(utterance);
    setTts(true);
  };
  const generator = route === "/app/gerador" || route === "/",
    historyPage = route === "/app/sinal-analise",
    brokerPage = route === "/brokers",
    localMode = integration?.data_source === 'polarium_independent';
  if (!boot && !user)
    return (
      <main className="page">
        <section className="card notice">
          <AlertCircle />
          <div>
            <h1>Serviço local indisponível</h1>
            <p>
              Não foi possível recuperar sua sessão. Confira se o servidor está
              em execução.
            </p>
            <button className="secondary" onClick={() => location.reload()}>
              Tentar novamente
            </button>
          </div>
        </section>
      </main>
    );
  return (
    <>
      <header className="topbar">
        <button className="brand" onClick={() => go("/app")}>
          <img src="/logo.webp" alt="" />
          <span>
            <strong>TRADER</strong>
            <small>Gerador + Polarium</small>
          </span>
        </button>
        <div className="header-right">
          <span className="source-tag">
            <i
              className={
                (localMode ? independentStatus?.connected || integration?.linked : integration?.nextgreen_connected) && connected
                  ? "dot online"
                  : "dot"
              }
            />
            {localMode
              ? independentStatus?.connected || integration?.linked ? 'Polarium direta · Demo' : 'Polarium desconectada'
              : integration?.nextgreen_connected ? 'NEXTGREEN conectado' : 'NEXTGREEN desconectado'}
          </span>
          <button className="outline return" onClick={() => go("/app")}>
            <ArrowLeft size={15} /> Voltar ao menu
          </button>
          {onLogout?<div className="account-access"><span>{user?.name}</span><button className="secondary" title={localMode?'Sair do painel mantém a busca automática no servidor. Para interromper, use Parar busca.':'Sair do painel'} disabled={busy} onClick={()=>action(onLogout)}><LogOut size={15}/> Sair</button></div>:null}
        </div>
      </header>
      {error && (
        <div className="toast" role="alert">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button
            className="icon"
            aria-label="Fechar alerta"
            onClick={() => setError("")}
          >
            <X size={16} />
          </button>
        </div>
      )}
      {boot ? (
        <div className="boot">
          <LoaderCircle className="spin" />
          Carregando sessão local…
        </div>
      ) : generator && localMode ? (
        <LocalGenerator
          onStatus={setIndependentStatus}
          onBack={() => go("/app")}
        />
      ) : generator ? (
        <Generator
          state={state}
          busy={busy}
          integration={integration}
          onSourceConnect={(credentials) => action(async () => {
            await post(credentials.cookie ? '/sources/nextgreen/session' : '/sources/nextgreen/login', credentials);
            setIntegration(await api('/brokers/integration'));
            await load();
          })}
          onSourceDisconnect={() => action(async () => {
            await api('/sources/nextgreen', {method:'DELETE'});
            setIntegration(await api('/brokers/integration'));
            await load();
          })}
          onStart={(input) => action(async () => setState(await post('/generator/start', input)))}
          onCancel={() => action(cancel)}
          onReplay={() => action(async () => {
            await post('/generator/replay-result', {signal_id:state.signal.signal_id});
            await load();await refreshScore();setDialog('result');
          })}
          onResult={() => setDialog('result')}
          onSchedule={(settings,confirmReal) => action(async () => {
            await post('/generator/schedule-entry', {signal_id:state.signal.signal_id,approved:true,settings,confirm_real:confirmReal});
            await load();
          })}
          onEntryRefresh={(signalId) => action(async () => {
            await post('/generator/entry-status', {signal_id:signalId});await load();
          })}
          onSpeak={speak}
          tts={tts}
          onBroker={() => go('/brokers')}
          onHistory={() => go('/app/sinal-analise')}
          onBack={() => go('/app')}
        />
      ) : historyPage ? (
        <HistoryPage user={user} onBack={() => go("/app")} onError={setError} />
      ) : brokerPage && localMode ? (
        <LocalBrokerPage
          integration={integration}
          onBack={() => go("/app")}
          onGenerator={() => go('/app/gerador')}
        />
      ) : brokerPage ? (
        <BrokerPage integration={integration} onChange={setIntegration} onError={setError} onBack={() => go('/app')}/>
      ) : route === "/auto-pilot" ? (
        <main className="page">
          <PageHeading
            title="Auto Pilot"
            subtitle="Motor adicional"
            onBack={() => go("/app")}
          />
          <section className="card notice">
            <AlertCircle />
            <div>
              <h2>Funcionalidade indisponível</h2>
              <p>
                Os motores e permissões do Auto Pilot não foram confirmados na
                investigação. Nenhuma estratégia adicional foi ativada.
              </p>
            </div>
          </section>
        </main>
      ) : (
        <Menu onGo={go} localMode={localMode} />
      )}
      {generator && !boot && (
        <div className={`score-widget ${pinned ? "pinned" : ""}`}>
          <img className="mascot" src="/mascot.webp" alt="" />
          <div className="score-top">
            <span title="Um resultado por ciclo. LOSS no último nível ou quando a recuperação é encerrada após uma perda confirmada. Ordens incertas ficam pendentes.">{score.source==='demo_cycles'?'CICLOS DEMO · HOJE':'ESTATÍSTICAS · MONITORAMENTO'}</span>
            <button
              onClick={() => {
                localStorage.setItem("score-pinned", String(!pinned));
                setPinned(!pinned);
              }}
            >
              <Pin size={11} />
              {pinned ? "SOLTAR" : "FIXAR"}
            </button>
            <button onClick={() => setDialog("reset")}>ZERAR</button>
          </div>
          <div className="score-values">
            <div className="win">
              WIN <strong>{score.win}</strong>
            </div>
            <div className="loss">
              LOSS <strong>{score.loss}</strong>
            </div>
          </div>
          <div className="score-bottom">
            <button
              aria-label="Abrir análise de sinais"
              onClick={() => go("/app/sinal-analise")}
            >
              <ArrowUpRight size={16} />
            </button>
            <button
              aria-label="Abrir histórico"
              onClick={() => go("/app/sinal-analise")}
            >
              <ArrowDownRight size={16} />
            </button>
          </div>
        </div>
      )}
      <nav className="mobile-nav" aria-label="Navegação">
        <button
          onClick={() => go("/app")}
          className={route === "/app" ? "current" : ""}
        >
          <LayoutDashboard />
          Painel
        </button>
        <button
          onClick={() => go("/brokers")}
          className={brokerPage ? "current" : ""}
        >
          <Link />
          Corretora
        </button>
        <button
          onClick={() => go("/app/gerador")}
          className={generator ? "current" : ""}
        >
          <Zap />
          Gerador
        </button>
        <button
          onClick={() => go("/app/sinal-analise")}
          className={historyPage ? "current" : ""}
        >
          <History />
          Análise
        </button>
        <button onClick={() => go("/auto-pilot")}>
          <Settings />
          Pilot
        </button>
      </nav>
      {dialog === "result" && state.signal && (
        <Modal title="Resultado do sinal" onClose={() => setDialog(null)}>
          <div
            className={`result-big ${state.signal.resultado?.toLowerCase()}`}
          >
            {state.signal.resultado || "PENDENTE"}
          </div>
          <p>
            {state.signal.ativo_display} · {state.signal.timeframe} ·{" "}
            {state.signal.direcao}
          </p>
          <p>
            {state.signal.data_source === "nextgreen"
              ? "Resultado informado pelo NEXTGREEN"
              : "Resultado histórico capturado"}{" "}
            · G{state.signal.martingale_aplicado || 0}
          </p>
          <div className="result-prices">
            <span>
              Abertura <b>{state.signal.preco_abertura ?? "—"}</b>
            </span>
            <span>
              Fechamento <b>{state.signal.preco_fechamento ?? "—"}</b>
            </span>
          </div>
          <p className="muted">
            Resultado informativo do sinal; ordens e liquidação são acompanhadas
            separadamente. Fonte:{" "}
            {state.signal.data_source === "nextgreen"
              ? "NEXTGREEN"
              : "reprodução histórica"}
            .
          </p>
          <button
            className="primary"
            onClick={() => {
              setDialog(null);
              action(cancel);
            }}
          >
            Nova Análise
          </button>
        </Modal>
      )}
      {dialog === "reset" && (
        <Modal title="Zerar placar de hoje?" onClose={() => setDialog(null)}>
          <p>Os ciclos concluídos deixarão de contar no placar de hoje. O histórico será preservado; ciclos ainda sem resultado contarão quando forem concluídos.</p>
          <div className="actions">
            <button className="secondary" onClick={() => setDialog(null)}>
              Cancelar
            </button>
            <button
              className="primary"
              onClick={() =>
                action(async () => {
                  setScore(await post("/mascot/score/reset"));
                  setDialog(null);
                })
              }
            >
              Zerar placar
            </button>
          </div>
        </Modal>
      )}
      {generator && !boot && <InstallBanner />}
    </>
  );
}
function Generator({
  state,
  busy,
  integration,
  onSourceConnect,
  onSourceDisconnect,
  onStart,
  onCancel,
  onReplay,
  onResult,
  onSchedule,
  onEntryRefresh,
  onSpeak,
  tts,
  onBroker,
  onHistory,
  onBack,
}) {
  const [timeframe, setTimeframe] = useState(
      localStorage.getItem("timeframe") || "M1",
    ),
    [mercado, setMercado] = useState(localStorage.getItem("mercado") || "otc"),
    [room, setRoom] = useState(true),
    [width, setWidth] = useState(
      Number(localStorage.getItem("panel-width")) || 440,
    ),
    [copied, setCopied] = useState(false);
  const drag = useRef(null),
    signal = state.signal,
    entryPending =
      state.auto_entry &&
      !["done", "error", "cancelled"].includes(state.auto_entry.phase),
    active = activeStates.includes(state.status);
  useEffect(() => {
    const move = (e) => {
      if (!drag.current) return;
      const next = Math.max(280, Math.min(innerWidth * 0.65, e.clientX));
      setWidth(next);
      localStorage.setItem("panel-width", String(next));
    };
    const end = () => {
      drag.current = null;
      document.body.style.cursor = "";
    };
    addEventListener("pointermove", move);
    addEventListener("pointerup", end);
    return () => {
      removeEventListener("pointermove", move);
      removeEventListener("pointerup", end);
    };
  }, []);
  const selectTimeframe = (value) => {
      setTimeframe(value);
      localStorage.setItem("timeframe", value);
    },
    selectMarket = (value) => {
      setMercado(value);
      localStorage.setItem("mercado", value);
    };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(
        `${signal.ativo_display} · ${signal.direcao} · ${signal.timeframe}\nConfirmação: ${signal.confirmacao.toFixed(1)}%\nEntrada: ${signal.proxima_vela.data_formatada}\nFonte: ${signal.data_source === "nextgreen" ? "NEXTGREEN autenticado" : "reprodução histórica — não é oportunidade atual"}.`,
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <main
      className={`generator ${room ? "split" : "wide"}`}
      style={{ "--panel-width": `${signal ? width : 280}px` }}
    >
      <div className="mobile-brand">
        <img src="/logo.webp" alt="NEXTGREEN" />
      </div>
      <button className="secondary mobile-back" onClick={onBack}>
        <ArrowLeft size={16} />
        Voltar
      </button>
      <aside className="generator-panel">
        {!signal ? (
          <section className="card config">
            <h1>
              <Zap size={20} className="amber" />
              Gerador NEXTGREEN
            </h1>
            <p className="muted intro">
              {integration?.data_source === "replay"
                ? "Teste histórico — amostras de 12 e 13/10/2026"
                : "Busca atual para a próxima vela"}
            </p>
            {integration?.data_source === "nextgreen" && (
              <SourceConnection
                integration={integration}
                busy={busy || active || !!entryPending}
                onConnect={onSourceConnect}
                onDisconnect={onSourceDisconnect}
              />
            )}
            <fieldset disabled={active || busy}>
              <legend>Selecione o Timeframe:</legend>
              <div className="choices">
                <button
                  aria-pressed={timeframe === "M1"}
                  className={timeframe === "M1" ? "selected" : ""}
                  onClick={() => selectTimeframe("M1")}
                >
                  M1 (1 minuto)
                </button>
                <button
                  aria-pressed={timeframe === "M5"}
                  className={timeframe === "M5" ? "selected" : ""}
                  onClick={() => selectTimeframe("M5")}
                >
                  M5 (5 minutos)
                </button>
              </div>
            </fieldset>
            <fieldset disabled={active || busy}>
              <legend>Mercado:</legend>
              <div className="choices">
                <button
                  aria-pressed={mercado === "otc"}
                  className={mercado === "otc" ? "selected" : ""}
                  onClick={() => selectMarket("otc")}
                >
                  Mercado OTC
                </button>
                <button
                  aria-pressed={mercado === "open"}
                  className={mercado === "open" ? "selected" : ""}
                  onClick={() => selectMarket("open")}
                >
                  Mercado Aberto
                </button>
              </div>
            </fieldset>
            <div className="gale-note">
              <p>
                <b>Filtro do vídeo Bruno SS: confirmação acima de 80%.</b> Os
                limites internos de RSI/ADX e o ranking do original continuam
                não confirmados.
              </p>
              O resultado do sinal é consolidado monitorando até <b>G1</b>{" "}
              (entrada inicial + 1 martingale na vela seguinte em caso de LOSS).
            </div>
            <button
              className="primary search"
              disabled={
                active ||
                busy ||
                !!entryPending ||
                !integration?.analysis_available
              }
              onClick={() =>
                onStart({ broker: "polarium", mercado, timeframe })
              }
            >
              {active ? (
                <LoaderCircle size={20} className="spin" />
              ) : (
                <Search size={19} />
              )}{" "}
              {active ? "Analisando…" : "Buscar Melhor Ativo"}
            </button>
            {integration?.analysis_available === false && (
              <p className="state-message" role="status">
                {integration.reason}
              </p>
            )}
            {active && (
              <div className="progress-state" role="status">
                <p>
                  {state.status === "connecting"
                    ? "Conectando à fonte…"
                    : state.status === "getting_actives"
                      ? "Aguardando os ativos na fonte…"
                      : state.message ||
                        `Analisando ${state.progress?.ativo || "ativos"}…`}
                </p>
                {state.progress && (
                  <progress max="100" value={state.progress?.percentual || 0} />
                )}
                <span>
                  {state.progress
                    ? `${state.progress.atual} de ${state.progress.total} ativos disponíveis`
                    : "A seleção é realizada na fonte autenticada."}
                </span>
                <button className="secondary" onClick={onCancel}>
                  Cancelar análise
                </button>
              </div>
            )}
            {["error", "no_opportunity"].includes(state.status) && (
              <div className="state-message" role="status">
                <AlertCircle size={18} />
                {state.error || "Nenhuma oportunidade disponível."}
              </div>
            )}
            {state.auto_entry && (
              <AutoEntryCard
                entry={state.auto_entry}
                integration={integration}
                busy={busy}
                onRefresh={onEntryRefresh}
                onBroker={onBroker}
              />
            )}
          </section>
        ) : (
          <>
            <div
              className={`direction ${signal.direcao === "PUT" ? "put" : ""}`}
            >
              {signal.direcao === "CALL" ? (
                <ArrowUpRight size={34} />
              ) : (
                <ArrowDownRight size={34} />
              )}
              <strong>{signal.direcao}</strong>
            </div>
            <section className="card signal-details">
              {[
                ["Ativo:", signal.ativo_display],
                ["Confirmação:", `${signal.confirmacao.toFixed(1)}%`],
                ["Timeframe:", signal.timeframe],
                ["Horário de Entrada:", signal.horario_entrada],
              ].map(([label, value]) => (
                <div key={label}>
                  <span>{label}</span>
                  <b>{value}</b>
                </div>
              ))}
            </section>
            <Chart signal={signal} />
            <section className="card analysis">
              <h2>🤖 Análise do sinal</h2>
              <p>
                ✨ O sinal encontrado foi o <b>{signal.ativo_display}!</b>
              </p>
              <p>
                {signal.data_source === "nextgreen"
                  ? "🔍 Sinal atual selecionado pelo NEXTGREEN autenticado. Ativo, direção, métricas e horário vêm do original."
                  : "🔍 Reprodução verificável do sinal capturado no NEXTGREEN. As regras de seleção entre ativos ainda não foram confirmadas."}
              </p>
              <p>
                📊 Índice de confirmação:{" "}
                <b>{signal.confirmacao.toFixed(1)}%</b>. Este índice não é
                probabilidade de ganho.
              </p>
              <p>
                📐 RSI em <b>{friendly(signal.indicadores.rsi)}</b> e ADX em{" "}
                <b>{friendly(signal.indicadores.adx)}</b> — direção{" "}
                {signal.indicadores.adx_direction}.
              </p>
              <p>
                🕯 Nas últimas velas:{" "}
                <b>{friendly(signal.percentuais.percentual_verdes)}%</b> verdes
                e <b>{friendly(signal.percentuais.percentual_vermelhas)}%</b>{" "}
                vermelhas.
              </p>
              <div className="indicator-flags">
                <span>
                  RSI:{" "}
                  {signal.indicadores.confirmacao_rsi
                    ? "confirmado"
                    : "não confirmado"}
                </span>
                <span>
                  ADX:{" "}
                  {signal.indicadores.confirmacao_adx
                    ? "confirmado"
                    : "não confirmado"}
                </span>
              </div>
              <button className="secondary" onClick={onSpeak}>
                {tts ? <VolumeX size={16} /> : <Volume2 size={16} />}{" "}
                {tts ? "Parar leitura" : "Ouvir análise"}
              </button>
            </section>
            <section className="card metrics">
              <h2>Indicadores técnicos</h2>
              <div className="metric-grid">
                <div>
                  <small>
                    {signal.data_source === "nextgreen" ? "RSI" : "RSI (14)"}
                  </small>
                  <b>{friendly(signal.indicadores.rsi)}</b>
                </div>
                <div>
                  <small>ADX</small>
                  <b>{friendly(signal.indicadores.adx)}</b>
                </div>
                <div>
                  <small>+DI</small>
                  <b>{friendly(signal.indicadores.plus_di)}</b>
                </div>
                <div>
                  <small>−DI</small>
                  <b>{friendly(signal.indicadores.minus_di)}</b>
                </div>
              </div>
              <div className="colors-bar">
                <i
                  style={{ width: `${signal.percentuais.percentual_verdes}%` }}
                />
                <i
                  style={{
                    width: `${signal.percentuais.percentual_vermelhas}%`,
                  }}
                />
                <i
                  style={{ width: `${signal.percentuais.percentual_cinzas}%` }}
                />
              </div>
              <p className="muted">
                {signal.percentuais.verdes} verdes ·{" "}
                {signal.percentuais.vermelhas} vermelhas ·{" "}
                {signal.percentuais.cinzas} cinzas · 15 velas
              </p>
            </section>
            <OriginalCriteria signal={signal} />
            <AutoEntryCard
              signal={signal}
              entry={state.auto_entry}
              integration={integration}
              busy={busy}
              onSchedule={onSchedule}
              onRefresh={onEntryRefresh}
              onBroker={onBroker}
            />
            <section className="card monitor">
              <h2>Monitoramento do sinal</h2>
              {signal.resultado ? (
                <>
                  <div className={`outcome ${signal.resultado.toLowerCase()}`}>
                    {signal.resultado} · G{signal.martingale_aplicado || 0}
                  </div>
                  <p className="muted">
                    {signal.data_source === "nextgreen"
                      ? "Resultado do sinal informado pelo NEXTGREEN. A execução aprovada tem acompanhamento separado no card de entrada."
                      : "Resultado histórico capturado, sem execução financeira."}
                  </p>
                  <button className="secondary" onClick={onResult}>
                    Ver resultado
                  </button>
                </>
              ) : signal.data_source === "nextgreen" ? (
                <p className="muted" role="status">
                  {state.monitor_error ||
                    "Aguardando o resultado informativo no NEXTGREEN…"}
                </p>
              ) : (
                <>
                  <p className="muted">
                    Reproduza o resultado registrado na investigação.
                  </p>
                  <button
                    className="primary"
                    disabled={busy || signal.timeframe === "M5"}
                    onClick={onReplay}
                  >
                    Reproduzir resultado capturado
                  </button>
                  {signal.timeframe === "M5" && (
                    <p className="replay-notice">
                      O resultado M5 não foi capturado.
                    </p>
                  )}
                </>
              )}
            </section>
            <div className="signal-actions">
              <button
                className="secondary"
                onClick={onCancel}
                disabled={busy || !!entryPending}
              >
                <RotateCcw size={16} />
                Nova Análise
              </button>
              <button className="secondary" onClick={copy}>
                <Copy size={16} />
                {copied ? "Copiado" : "Copiar sinal"}
              </button>
              <button className="text-button" onClick={onHistory}>
                Análise de Sinais <ChevronRight size={15} />
              </button>
              <button
                className="text-button muted"
                onClick={onCancel}
                disabled={!!entryPending}
              >
                Cancelar análise
              </button>
            </div>
          </>
        )}
        {state.report?.length > 0 && (
          <details className="scan-report">
            <summary>
              {state.source === "nextgreen"
                ? "Sinal retornado pela fonte"
                : `Ativos avaliados (${state.report.length})`}
            </summary>
            {state.report.map((x) => (
              <div key={x.ativo}>
                <b>{x.ativo}</b>
                <small>
                  {x.status === "approved"
                    ? state.source === "nextgreen"
                      ? "Sinal original aceito"
                      : "Amostra reproduzida"
                    : x.status === "failed"
                      ? "Falha"
                      : "Rejeitado"}
                </small>
                <p>{x.reason}</p>
              </div>
            ))}
          </details>
        )}
      </aside>
      <div
        className="resize-handle"
        role="separator"
        aria-label="Redimensionar painel"
        aria-orientation="vertical"
        tabIndex={0}
        onPointerDown={(e) => {
          drag.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          document.body.style.cursor = "col-resize";
        }}
        onKeyDown={(e) => {
          if (["ArrowLeft", "ArrowRight"].includes(e.key)) {
            e.preventDefault();
            const next = Math.max(
              280,
              Math.min(
                innerWidth * 0.65,
                width + (e.key === "ArrowRight" ? 20 : -20),
              ),
            );
            setWidth(next);
            localStorage.setItem("panel-width", String(next));
          }
        }}
      />
      {room && (
        <section className="broker-room">
          <div className="room-title">
            <b>Polarium</b>
            <button onClick={() => setRoom(false)}>
              <X size={13} />
              Ocultar sala
            </button>
          </div>
          <iframe
            src="https://trade.polariumbroker.com/traderoom/"
            title="Sala da Polarium — serviço externo"
            referrerPolicy="strict-origin-when-cross-origin"
          />
          <div className="room-footer">
            Sala externa da Polarium · vínculo e sessão independentes do gerador
            local.
          </div>
        </section>
      )}
      {!room && (
        <button className="outline show-room" onClick={() => setRoom(true)}>
          <ExternalLink size={15} />
          Mostrar sala Polarium
        </button>
      )}
    </main>
  );
}
function OriginalCriteria({ signal }) {
  const audit = auditOriginalCriteria(signal);
  const labels = {
    matched: "Conferido",
    mismatch: "Divergente",
    reported: "Retorno do original",
    unknown: "Não confirmado",
  };
  return (
    <section className="card original-criteria">
      <h2>Critérios observados no NEXTGREEN</h2>
      <p className="muted">
        Mesmas métricas do sinal capturado. Cada item mostra o que foi possível
        verificar.
      </p>
      <ul>
        {audit.checks.map((check) => (
          <li key={check.id}>
            <div className="criteria-heading">
              <b>{check.label}</b>
              <span className={`criteria-status ${check.status}`}>
                {labels[check.status]}
              </span>
            </div>
            {check.value !== null && check.value !== undefined && (
              <strong className="criteria-value">
                {friendly(check.value)}
                {["base", "minimum", "operator_preference"].includes(check.id)
                  ? "%"
                  : check.id === "adjustment"
                    ? " pontos"
                    : ""}
                {check.flag !== undefined && check.flag !== null
                  ? ` · ${check.flag ? "confirmado" : "não confirmou"}`
                  : ""}
              </strong>
            )}
            <p>{check.detail}</p>
          </li>
        ))}
      </ul>
      <p className="criteria-note">
        {signal.data_source === "nextgreen"
          ? "Este sinal foi selecionado pelo NEXTGREEN. Seus filtros privados não foram reconstruídos no projeto."
          : "A seleção em tempo real permanece indisponível até confirmar os filtros e o ranking do algoritmo original."}
      </p>
    </section>
  );
}
function AutoEntryCard({
  signal,
  entry,
  integration,
  busy,
  onSchedule,
  onRefresh,
  onBroker,
}) {
  const [clock, setClock] = useState(Date.now());
  const [confirm, setConfirm] = useState(null);
  useEffect(() => {
    const timer = setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => setConfirm(null), [signal?.signal_id]);
  const settings = entry?.settings || integration?.auto_entry;
  const timestamp = entry?.entry_timestamp ?? signal?.proxima_vela?.timestamp;
  const expired = !timestamp || clock > timestamp * 1000 - 500;
  const ready =
    signal?.data_source === "nextgreen" &&
    !signal.resultado &&
    !entry &&
    integration?.execution_available &&
    !expired;
  const date = timestamp
    ? new Date(timestamp * 1000).toLocaleString("pt-BR", {
        timeZone: "America/Sao_Paulo",
        dateStyle: "short",
        timeStyle: "short",
      })
    : "—";
  const phaseLabels = {
    preparing: "Conferindo configurações",
    submitting: "Enviando aprovação",
    waiting: "Entrada agendada",
    executing: "Executando G0",
    monitoring: "Aguardando resultado da ordem",
    martingale_waiting: "Aguardando G1",
    martingale_executing: "Executando G1",
    done: "Ciclo encerrado",
    error: "Agendamento recusado",
    cancelled: "Agendamento cancelado",
    unknown: "Status a confirmar",
  };
  return (
    <section className="card entry">
      <h2>Entrada automática</h2>
      <p>{date} · Brasília</p>
      <p className="muted">
        Polarium via NEXTGREEN ·{" "}
        {entry
          ? phaseLabels[entry.phase] || entry.phase
          : expired
            ? "prazo de aprovação encerrado"
            : "aguardando sua aprovação"}
        .
      </p>
      <div className="entry-summary">
        <span>
          Conta <b>{settings?.account_mode === "real" ? "Real" : "Demo"}</b>
        </span>
        <span>
          Entrada <b>{money(settings?.amount || 5)}</b>
        </span>
        <span>
          G1{" "}
          <b>
            {settings?.martingale
              ? money((settings.amount || 5) * 2)
              : "Desativado"}
          </b>
        </span>
      </div>
      {!entry && (
        <>
          <button
            className="primary"
            disabled={busy || !ready}
            onClick={() =>
              settings.account_mode === "real"
                ? setConfirm({ ...settings })
                : onSchedule({ ...settings }, false)
            }
          >
            {busy
              ? "Processando…"
              : expired
                ? "Prazo de aprovação encerrado"
                : "Aprovar entrada automática"}
          </button>
          {!integration?.execution_available && (
            <p className="state-message">
              {integration?.execution_reason ||
                "Conecte a fonte NEXTGREEN e vincule a Polarium no original."}
            </p>
          )}
          <p className="muted">
            A aprovação vale somente para este sinal.{" "}
            {settings?.martingale
              ? "Se G0 fechar LOSS, a fonte executa G1 com valor dobrado no mesmo sinal."
              : "Sem martingale."}
          </p>
          <button className="text-button" onClick={onBroker}>
            Alterar conta, entrada e G1 <Settings size={14} />
          </button>
        </>
      )}
      {entry && (
        <div role="status" className="gale-note">
          <b>{phaseLabels[entry.phase] || entry.phase}</b>
          <p>{entry.message}</p>
          {entry.transaction_id != null && (
            <p>
              Ordem G0: <b>{String(entry.transaction_id)}</b>
              {entry.result_first ? ` · ${entry.result_first}` : ""}
            </p>
          )}
          {entry.gale_transaction_id != null && (
            <p>
              Ordem G1: <b>{String(entry.gale_transaction_id)}</b>
              {entry.result_gale ? ` · ${entry.result_gale}` : ""}
            </p>
          )}
          {entry.financial_result && (
            <p>
              Resultado da execução: <b>{entry.financial_result}</b>
            </p>
          )}
          {["unknown", "done", "error"].includes(entry.phase) && (
            <button
              className="secondary"
              disabled={busy}
              onClick={() => onRefresh(entry.signal_id)}
            >
              Atualizar status da entrada
            </button>
          )}
        </div>
      )}
      {confirm && (
        <Modal
          title="Confirmar entrada na conta Real"
          onClose={() => setConfirm(null)}
        >
          <p>
            {signal.ativo_display} · {signal.direcao} · {signal.timeframe} ·{" "}
            {date}
          </p>
          <p>
            Entrada: <b>{money(confirm.amount)}</b>. G1:{" "}
            <b>
              {confirm.martingale ? money(confirm.amount * 2) : "desativado"}
            </b>
            .
          </p>
          <p>
            Esta aprovação agenda a execução na Polarium vinculada ao NEXTGREEN.
          </p>
          <div className="actions">
            <button className="secondary" onClick={() => setConfirm(null)}>
              Cancelar
            </button>
            <button
              className="primary"
              disabled={busy || !ready}
              onClick={() => {
                onSchedule(confirm, true);
                setConfirm(null);
              }}
            >
              Confirmar e agendar na conta Real
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}
function SourceConnection({ integration, busy, onConnect, onDisconnect }) {
  const [email, setEmail] = useState(""),
    [password, setPassword] = useState("");
  const [session, setSession] = useState("");
  return (
    <section className="source-connection">
      <h2>Fonte NEXTGREEN</h2>
      {integration.nextgreen_connected ? (
        <>
          <p>Sessão conectada · sinais originais</p>
          <button className="secondary" disabled={busy} onClick={onDisconnect}>
            Desconectar fonte
          </button>
        </>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onConnect({ email, password }).finally(() => setPassword(""));
          }}
        >
          <label htmlFor="source-email">E-mail NEXTGREEN</label>
          <input
            id="source-email"
            type="email"
            required
            value={email}
            autoComplete="off"
            onChange={(e) => setEmail(e.target.value)}
          />
          <label htmlFor="source-password">Senha NEXTGREEN</label>
          <input
            id="source-password"
            type="password"
            required
            value={password}
            autoComplete="off"
            onChange={(e) => setPassword(e.target.value)}
          />
          <button className="secondary" disabled={busy}>
            Conectar fonte original
          </button>
          <p className="muted">
            Senha não gravada. A sessão fica na memória do servidor até
            desconectar ou reiniciar.
          </p>
          <details>
            <summary>Usar uma sessão já autenticada</summary>
            <label htmlFor="source-session">Sessão NEXTGREEN</label>
            <input
              id="source-session"
              type="password"
              value={session}
              autoComplete="off"
              onChange={(e) => setSession(e.target.value)}
            />
            <button
              type="button"
              className="secondary"
              disabled={busy || !session}
              onClick={() =>
                onConnect({ cookie: session }).finally(() => setSession(""))
              }
            >
              Conectar sessão atual
            </button>
          </details>
        </form>
      )}
    </section>
  );
}
function Menu({ onGo, localMode }) {
  return (
    <main className="page menu">
      <div className="page-heading">
        <div>
          <p className="eyebrow">TRADER · AMBIENTE LOCAL</p>
          <h1>Painel de ferramentas</h1>
          <p className="muted">
            {localMode?'Algoritmo local e acompanhamento das suas operações.':'Sinais atuais do NEXTGREEN e acompanhamento das suas operações.'}
          </p>
        </div>
      </div>
      <div className="menu-grid">
        {[
          {
            path: "/app/gerador",
            icon: Zap,
            title: localMode ? 'Gerador local' : 'Gerador NEXTGREEN',
            text: localMode ? 'M1 · OTC · execução Demo' : 'M1 e M5 · OTC e mercado aberto',
          },
          {
            path: "/app/sinal-analise",
            icon: ChartNoAxesCombined,
            title: "Análise de Sinais",
            text: "Histórico, resultados e indicadores",
          },
          {
            path: "/brokers",
            icon: Link,
            title: "Polarium",
            text: "Integração e configuração de entrada",
          },
          {
            path: "/auto-pilot",
            icon: Settings,
            title: "Auto Pilot",
            text: "Motor adicional não confirmado",
          },
        ].map((x) => (
          <button
            className="card menu-item"
            key={x.path}
            onClick={() => onGo(x.path)}
          >
            <x.icon size={30} />
            <h2>{x.title}</h2>
            <p>{x.text}</p>
            <ChevronRight size={19} />
          </button>
        ))}
      </div>
      <div className="card notice">
        <AlertCircle />
        <div>
          <b>{localMode?'Algoritmo local experimental':'Fonte original NEXTGREEN'}</b>
          <p>
            {localMode
              ? 'A busca usa dados diretos da Polarium e regras calculadas no próprio servidor. As entradas automáticas operam em conta Demo.'
              : 'Conecte sua conta NEXTGREEN para receber os sinais originais com o mesmo ativo, direção, indicadores e horário. Cada entrada exige aprovação do sinal.'}
          </p>
        </div>
      </div>
    </main>
  );
}
function BrokerPage({ integration, onChange, onError, onBack }) {
  const [settings, setSettings] = useState(
      integration?.auto_entry || {
        amount: 5,
        account_mode: "demo",
        martingale: true,
      },
    ),
    [saved, setSaved] = useState(false),
    [saving, setSaving] = useState(false),
    [confirmReal, setConfirmReal] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const result = await api("/brokers/integration/auto-entry", {
        method: "PUT",
        body: settings,
      });
      setSettings(result);
      onChange({ ...integration, auto_entry: result });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      onError(e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <main className="page">
      <PageHeading
        title="Integração de corretora"
        subtitle="Gerencie sua conexão e configurações de entrada."
        onBack={onBack}
      />
      <div className="broker-grid">
        <section className="card broker-card">
          <div className="broker-brand">
            <span className="broker-logo">P</span>
            <div>
              <h2>Polarium</h2>
              <span className="status-pill">
                {integration?.linked
                  ? "Vinculada no NEXTGREEN"
                  : "Não vinculada"}
              </span>
            </div>
          </div>
          <div className="notice">
            <AlertCircle size={22} />
            <div>
              <b>
                {integration?.linked
                  ? "Execução pela fonte original"
                  : "Vínculo necessário no NEXTGREEN"}
              </b>
              <p>
                {integration?.execution_reason ||
                  "Conecte a fonte no gerador e vincule a Polarium no NEXTGREEN. O projeto usa essa integração para aprovar cada sinal."}
              </p>
            </div>
          </div>
          <a
            className="primary"
            href="https://app.nextgreen.pro/brokers"
            target="_blank"
            rel="noreferrer"
          >
            Gerenciar vínculo no NEXTGREEN <ExternalLink size={14} />
          </a>
          <a
            className="text-button"
            href="https://trade.polariumbroker.com/traderoom/"
            target="_blank"
            rel="noreferrer"
          >
            Abrir sala da corretora <ExternalLink size={14} />
          </a>
        </section>
        <section className="card settings-card">
          <h2>Configuração de entrada</h2>
          <p className="muted">
            Conta, entrada e G1 serão conferidos e enviados ao NEXTGREEN ao
            aprovar cada sinal.
          </p>
          <fieldset>
            <legend>Tipo de conta</legend>
            <div className="choices">
              <button
                className={settings.account_mode === "demo" ? "selected" : ""}
                onClick={() =>
                  setSettings({ ...settings, account_mode: "demo" })
                }
              >
                Demo
              </button>
              <button
                className={settings.account_mode === "real" ? "selected" : ""}
                onClick={() => setConfirmReal(true)}
              >
                Real
              </button>
            </div>
          </fieldset>
          <label htmlFor="amount">Valor da entrada (USD)</label>
          <input
            id="amount"
            inputMode="decimal"
            value={settings.amount}
            onChange={(e) =>
              setSettings({ ...settings, amount: e.target.value })
            }
          />
          <small className="muted">Valor mínimo: 5. G1 dobra a entrada.</small>
          <label className="switch-label">
            <input
              type="checkbox"
              checked={settings.martingale}
              onChange={(e) =>
                setSettings({ ...settings, martingale: e.target.checked })
              }
            />
            <span>Martingale G1</span>
          </label>
          <div className="gale-note">
            Entrada:{" "}
            {money(Number(String(settings.amount).replace(",", ".")) || 5)} ·
            G1:{" "}
            {settings.martingale
              ? money(
                  (Number(String(settings.amount).replace(",", ".")) || 5) * 2,
                )
              : "desativado"}
          </div>
          <button className="primary" onClick={save} disabled={saving}>
            {saved ? <CheckCircle2 size={18} /> : <Settings size={17} />}{" "}
            {saved ? "Configurações salvas" : "Salvar configurações"}
          </button>
        </section>
      </div>
      {confirmReal && (
        <Modal
          title="Selecionar conta Real?"
          onClose={() => setConfirmReal(false)}
        >
          <p>
            As próximas aprovações usarão a conta Real e exigirão confirmação do
            sinal, valor e G1. Salvar esta preferência não envia uma ordem.
          </p>
          <div className="actions">
            <button className="secondary" onClick={() => setConfirmReal(false)}>
              Cancelar
            </button>
            <button
              className="primary"
              onClick={() => {
                setSettings({ ...settings, account_mode: "real" });
                setConfirmReal(false);
              }}
            >
              Selecionar Real
            </button>
          </div>
        </Modal>
      )}
    </main>
  );
}
function PageHeading({ title, subtitle, onBack }) {
  return (
    <div className="page-heading">
      <div>
        <button className="text-button" onClick={onBack}>
          <ArrowLeft size={15} />
          Voltar ao painel
        </button>
        <h1>{title}</h1>
        <p className="muted">{subtitle}</p>
      </div>
    </div>
  );
}
function HistoryPage({ user, onBack, onError }) {
  const today = saoDay(new Date()),
    [filters, setFilters] = useState({
      from: today,
      to: today,
      source: "all",
      timeframe: "all",
    }),
    [rows, setRows] = useState([]),
    [loading, setLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    api(`/user/${user.id}/signal-history?limit=1000&offset=0`)
      .then((data) => {
        if (mounted) setRows(data.signals);
      })
      .catch(onError)
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [user.id]);
  const filtered = useMemo(() => filterHistory(rows, filters), [rows, filters]),
    stats = useMemo(() => aggregate(filtered), [filtered]);
  const preset = (days) => {
    const d = new Date(`${today}T12:00:00`);
    d.setDate(d.getDate() - days + 1);
    setFilters({
      ...filters,
      from: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
      to: today,
    });
  };
  return (
    <main className="page history-page">
      <PageHeading
        title="Análise de Sinais"
        subtitle="Resultados e métricas do histórico · horário de Brasília"
        onBack={onBack}
      />
      <section className="card filters">
        <div className="presets">
          {[1, 7, 30].map((n) => (
            <button key={n} className="secondary" onClick={() => preset(n)}>
              {n === 1 ? "Hoje" : `${n} dias`}
            </button>
          ))}
        </div>
        <DatePicker
          id="from"
          label="Data inicial"
          value={filters.from}
          maxDate={filters.to}
          onChange={(from) => setFilters({ ...filters, from })}
        />
        <DatePicker
          id="to"
          label="Data final"
          value={filters.to}
          minDate={filters.from}
          onChange={(to) => setFilters({ ...filters, to })}
        />
        <div className="filter-field">
          <label htmlFor="source">Origem</label>
          <select
            id="source"
            value={filters.source}
            onChange={(e) => setFilters({ ...filters, source: e.target.value })}
          >
            <option value="all">Todos</option>
            <option value="gerador">Gerador</option>
            <option value="autopilot">Autopilot</option>
          </select>
        </div>
        <div className="filter-field">
          <label htmlFor="period">Timeframe</label>
          <select
            id="period"
            value={filters.timeframe}
            onChange={(e) =>
              setFilters({ ...filters, timeframe: e.target.value })
            }
          >
            <option value="all">Todos</option>
            <option>M1</option>
            <option>M5</option>
          </select>
        </div>
      </section>
      <div className="stats-grid">
        {[
          ["Total de sinais", stats.total, ""],
          ["WIN", stats.win, "win"],
          ["LOSS", stats.loss, "loss"],
          ["Taxa de acerto", `${friendly(stats.rate)}%`, "green"],
          ["Confirmação média", `${friendly(stats.confirmation)}%`, ""],
          ["Pendentes / DOJI", `${stats.pending} / ${stats.doji}`, ""],
        ].map(([label, value, color]) => (
          <section className="card stat" key={label}>
            <small>{label}</small>
            <strong className={color}>{value}</strong>
          </section>
        ))}
      </div>
      <div className="card stats-note">
        Taxa de acerto = WIN ÷ (WIN + LOSS). DOJI e pendentes ficam fora do
        denominador. A fonte de cada registro está indicada na tabela; consulta
        limitada a 1.000 registros.
      </div>
      <div className="history-summary">
        <section className="card">
          <h2>Sequência atual</h2>
          <p className="large">
            {stats.sequence}{" "}
            <span className={stats.sequenceResult?.toLowerCase()}>
              {stats.sequenceResult || "—"}
            </span>
          </p>
        </section>
        <section className="card">
          <h2>Melhor ativo</h2>
          <p className="large">{stats.best?.name || "—"}</p>
          <small className="muted">Mínimo de 3 resultados WIN/LOSS</small>
        </section>
        <section className="card">
          <h2>Pior ativo</h2>
          <p className="large">{stats.worst?.name || "—"}</p>
          <small className="muted">Mínimo de 3 resultados WIN/LOSS</small>
        </section>
      </div>
      <section className="card table-card">
        <h2>
          Histórico de sinais <span className="muted">({filtered.length})</span>
        </h2>
        {loading ? (
          <div className="empty">
            <LoaderCircle className="spin" />
            Carregando histórico…
          </div>
        ) : !filtered.length ? (
          <div className="empty">
            <History size={32} />
            <b>Nenhum sinal neste período</b>
            <p>Faça uma busca no gerador ou ajuste os filtros.</p>
          </div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Ativo</th>
                  <th>Direção</th>
                  <th>Timeframe</th>
                  <th>Confirmação</th>
                  <th>Resultado</th>
                  <th>Execução</th>
                  <th>Fonte</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.signal_id}>
                    <td>
                      {new Date(s.resultado_at || s.created_at).toLocaleString(
                        "pt-BR",
                        { timeZone: "America/Sao_Paulo" },
                      )}
                    </td>
                    <td>
                      <b>{s.ativo_display}</b>
                    </td>
                    <td className={s.direcao === "CALL" ? "green" : "loss"}>
                      {s.direcao}
                    </td>
                    <td>{s.timeframe}</td>
                    <td>{s.confirmacao.toFixed(1)}%</td>
                    <td>
                      <span
                        className={`result-chip ${s.resultado?.toLowerCase() || ""}`}
                      >
                        {s.resultado || "PENDENTE"}
                      </span>
                    </td>
                    <td>
                      {s.execution
                        ? s.execution.financial_result || s.execution.phase
                        : "Sem aprovação"}
                    </td>
                    <td>
                      {s.data_source === "nextgreen"
                        ? s.execution?.transaction_id != null
                          ? `NEXTGREEN · ordem ${s.execution.transaction_id}`
                          : s.execution
                            ? "NEXTGREEN · aprovação registrada"
                            : "NEXTGREEN · sem ordem"
                        : "Replay · sem ordem"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <div className="breakdowns">
        <GroupTable
          title="Ranking por ativo"
          groups={stats.assets.slice(0, 15)}
          empty="Mínimo de 2 resultados WIN/LOSS por ativo."
        />
        <GroupTable title="Por timeframe" groups={stats.byTimeframe} />
        <GroupTable title="Por direção" groups={stats.byDirection} />
        <GroupTable title="Por mês" groups={stats.byMonth} />
      </div>
    </main>
  );
}
function GroupTable({ title, groups, empty = "Nenhum dado no período." }) {
  return (
    <section className="card table-card">
      <h2>{title}</h2>
      {groups.length ? (
        <table>
          <thead>
            <tr>
              <th>Grupo</th>
              <th>Total</th>
              <th>WIN</th>
              <th>LOSS</th>
              <th>Acerto</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.name}>
                <td>{g.name}</td>
                <td>{g.total}</td>
                <td className="win">{g.win}</td>
                <td className="loss">{g.loss}</td>
                <td>{friendly(g.rate)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted">{empty}</p>
      )}
    </section>
  );
}
